import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Skeleton,
  Spinner,
  Text,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';

import {
  date as commonDate,
  finleyAssert,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { AssignmentPreviewJobFragmentGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import { EditAssignmentDropdown } from '../../EditAssignmentDropdown';
import { AssignmentSummary } from './AssignmentSummary';
import { PreviewJobsTable } from './PreviewJobsTable';
import { colors } from 'packages/common-frontend/colors';
import { Alert } from 'packages/common-frontend/components/Alert';
import { useLatestAssignmentPipelineRunMetadataForLpGroupingContext } from 'packages/loanProduct/hooks/LatestAssignmentPipelineRunMetadataForLpGroupingProvider';
import { usePollPreviewGenerationJobs } from 'packages/loanProduct/hooks/usePollPreviewGenerationJobs';
import { AssignmentSummaryConfirmButton } from 'packages/loanProduct/ui/AssignmentSummaryConfirmButton';
import {
  AssignmentHeader,
  AssignmentTemplate,
} from 'packages/loanProduct/ui/AssignmentTemplate';
import { CheckLatestPipelineRunStatus } from 'packages/loanProduct/ui/v2/CheckLatestPipelineRunStatus';

const AssignmentSummaryLoadingTemplate = () => {
  return (
    <AssignmentTemplate
      headerChildren={
        <HStack spacing="32px">
          <EditAssignmentDropdown isDisabled />
          <Button variant="solid" colorScheme="aurora" isDisabled>
            Confirm Assignments
          </Button>
        </HStack>
      }
    >
      <Skeleton data-testid="loading-assignment-summary-body" height="80vh" />
    </AssignmentTemplate>
  );
};

export const AssignmentDashboard = ({
  masterLoanTapeIds,
  organizationId,
  reportingDate,
  fileVersion,
}: {
  masterLoanTapeIds: string[];
  organizationId: string;
  reportingDate: Util.FullDateStr;
  fileVersion: number;
}) => {
  const [
    selectedPreviewJob,
    setSelectedPreviewJob,
  ] = useState<AssignmentPreviewJobFragmentGQLType | null>(null);

  const [
    initialPreviewJob,
    setInitialPreviewJob,
  ] = useState<AssignmentPreviewJobFragmentGQLType | null>(null);

  const { previewJobs } = usePollPreviewGenerationJobs({
    organizationId,
    reportingDate,
    fileVersion,
  });

  useEffect(() => {
    if (previewJobs && previewJobs.length > 0) {
      if (selectedPreviewJob) {
        const currentJob = previewJobs.find(
          (job) => job.id === selectedPreviewJob.id,
        );
        setSelectedPreviewJob(currentJob ?? previewJobs[0]);
      } else {
        setSelectedPreviewJob(previewJobs[0]);
      }
      if (!initialPreviewJob) {
        setInitialPreviewJob(previewJobs[previewJobs.length - 1]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewJobs]);

  if (!selectedPreviewJob || !previewJobs) {
    return <AssignmentSummaryLoadingTemplate />;
  }

  const generatePreviewTitle = () => {
    const assignmentMethod = selectedPreviewJob.assignmentMethod;
    const createdAt = commonDate.epochToLocaleDateString(
      selectedPreviewJob.created_at,
    );
    let methodText: string;
    switch (assignmentMethod) {
      case 'assignNone':
        methodText = 'Assign None';
        break;
      case 'csv':
        methodText = 'Assign by CSV';
        break;
      case 'strategy':
        // eslint-disable-next-line no-case-declarations
        const strategyName = selectedPreviewJob.strategyName;
        finleyAssert(
          strategyName !== undefined && typeof strategyName === 'string',
          `Expected preview job ${selectedPreviewJob.id} to have a defined associated assignment strategy name`,
        );
        methodText = strategyName;
        break;
      default:
        return finleyAssertUnreachable(assignmentMethod);
    }
    return `${methodText} - ${createdAt}`;
  };

  return (
    <Box height="100%">
      <Box pb="2rem" background={colors.white}>
        <AssignmentHeader editMode={false}>
          <HStack spacing="32px">
            <EditAssignmentDropdown />
            {selectedPreviewJob &&
            selectedPreviewJob.previewIdentifier &&
            selectedPreviewJob.status === 'completed' ? (
              <AssignmentSummaryConfirmButton
                masterLoanTapeIds={masterLoanTapeIds}
                previewIdentifier={selectedPreviewJob.previewIdentifier}
                reportingDate={reportingDate}
                fileVersion={fileVersion}
              />
            ) : (
              <Button variant="solid" colorScheme="aurora" isDisabled>
                Confirm Assignments
              </Button>
            )}
          </HStack>
        </AssignmentHeader>
      </Box>
      <Box>
        <PreviewJobsTable
          selectedPreviewJob={selectedPreviewJob}
          setSelectedPreviewJob={setSelectedPreviewJob}
          previewJobs={previewJobs}
        />
      </Box>

      <Heading size="md" mt="32px">
        {generatePreviewTitle()}
      </Heading>

      {selectedPreviewJob.status !== 'completed' && (
        <Box p="32px" mt="2rem" bg={colors.millet[50]}>
          {selectedPreviewJob.status === 'error' && (
            <Alert status="error" isClosable={false}>
              <Text>
                {`An error occurred while generating assignment preview. Review your
            CSV or assignment strategy and try again.`}
              </Text>
            </Alert>
          )}
          {selectedPreviewJob.status === 'inProgress' && (
            <Flex>
              <Spinner />
              <Heading ml="16px" size="md">
                Preview generation in progress
              </Heading>
            </Flex>
          )}
        </Box>
      )}

      {initialPreviewJob && selectedPreviewJob.status === 'completed' && (
        <AssignmentSummary
          organizationId={organizationId}
          reportingDate={reportingDate}
          fileVersion={fileVersion}
          initialPreviewJobId={initialPreviewJob.id}
          proposedPreviewJobId={selectedPreviewJob.id}
        />
      )}
    </Box>
  );
};

export const AssignmentDashboardContainer = ({
  organizationId,
}: {
  organizationId: string;
}) => {
  const {
    metadata,
  } = useLatestAssignmentPipelineRunMetadataForLpGroupingContext();

  return (
    <Box height="100%" w="100%">
      <CheckLatestPipelineRunStatus>
        {({ StatusAlert }) => {
          if (metadata === undefined) {
            return <AssignmentSummaryLoadingTemplate />;
          }
          if (!metadata || metadata.status !== 'success') {
            return (
              <AssignmentTemplate
                headerChildren={
                  <HStack spacing="32px">
                    <EditAssignmentDropdown />
                    <Button variant="solid" colorScheme="aurora" isDisabled>
                      Confirm Assignments
                    </Button>
                  </HStack>
                }
              >
                <StatusAlert
                  status={metadata ? metadata.status : 'noPipelineRun'}
                />
              </AssignmentTemplate>
            );
          }
          return (
            <AssignmentDashboard
              organizationId={organizationId}
              masterLoanTapeIds={metadata.masterLoanTapeIds}
              fileVersion={metadata.fileVersion}
              reportingDate={metadata.reportingDate}
            />
          );
        }}
      </CheckLatestPipelineRunStatus>
    </Box>
  );
};
